/**
 * One scan ingestion pipeline for the till (BUILD PROMPT Phase 2, S6.1/S6.3/S5.1).
 *
 * Every accepted source — keyboard-wedge hardware, phone camera, manual code entry and the
 * optional paired scanner — funnels through this coordinator. The rules, in one place:
 *
 * - Each scan is an event with a unique id. The raw barcode is preserved EXACTLY as scanned
 *   (leading zeros included); only the documented barcode rule below derives the lookup code.
 * - Bounded FIFO queue. Capture never blocks on lookups; lookups resolve strictly in ingest
 *   order (concurrency 1), so delayed/reordered transport cannot reorder or merge additions.
 * - Dedup is by EVENT id only: redelivery of one event adds nothing, while deliberately
 *   scanning the same physical barcode twice (two events) adds two units. There is NO global
 *   barcode debounce — it would drop legitimately repeated goods.
 * - A stationary camera observing one barcode is one presentation: repeat frames are
 *   suppressed until the code leaves or a deliberate scan-again clears the presentation.
 * - Every event carries its bill generation. When the bill changes (new bill, hold, resume,
 *   sign-out), the integrator advances the generation; pending lookups from the old bill
 *   resolve to a visible stale outcome and NEVER touch the new bill.
 * - Transport failures stay distinct: miss, timeout, offline, auth-expiry, forbidden and
 *   server-error are separate outcomes. A network failure is never reported as not-found.
 * - Queue overflow is a visible outcome (retry with a fresh event id), never a silent drop.
 *   Suppression and overflow outcomes emit synchronously at ingest; added/failure outcomes
 *   emit in ingest order once their lookup resolves.
 *
 * Zero dependencies. Framework-free so it unit-tests in plain vitest.
 */

import { ApiError, isNetworkError } from './api';

/** Accepted scan origins. Camera and paired frames arrive here; text search stays separate. */
export type ScanSource = 'hardware' | 'camera' | 'manual' | 'paired';

/** Lookup answers the coordinator understands. Adapters map transport errors via classify. */
export type LookupResult<T> =
  | { kind: 'found'; product: T }
  | { kind: 'not-found' }
  | { kind: 'timeout' }
  | { kind: 'offline' }
  | { kind: 'auth-expired' }
  | { kind: 'forbidden' }
  | { kind: 'server-error'; message?: string };

/** Transport-level failure kinds. Deliberately has no success/miss ambiguity. */
export type ScanFailureKind =
  | 'not-found'
  | 'timeout'
  | 'offline'
  | 'auth-expired'
  | 'forbidden'
  | 'server-error';

export type ScanOutcomeKind =
  | 'added'
  | ScanFailureKind
  | 'queue-overflow'
  | 'duplicate-suppressed'
  | 'stale-generation'
  | 'camera-repeat-suppressed';

export interface NewScanEvent {
  /** Exactly what the source read. Never normalised here; see normalizeBarcode. */
  raw: string;
  source: ScanSource;
  /** Unique per scan. Redelivery of an id is a duplicate, never a second unit. */
  eventId?: string;
  storeId?: string | null;
  terminalId?: string | null;
  sessionId?: string | null;
  /** Bill generation this scan belongs to. Defaults to the coordinator's current one. */
  cartGeneration?: number | null;
  receivedAt?: number;
}

export interface ScanEvent {
  eventId: string;
  /** Byte-exact source text, leading zeros and all. */
  raw: string;
  /** lookupCode is normalizeBarcode(raw): the ONLY normalisation in the pipeline. */
  lookupCode: string;
  source: ScanSource;
  storeId: string | null;
  terminalId: string | null;
  sessionId: string | null;
  cartGeneration: number;
  receivedAt: number;
  seq: number;
}

export interface ScanOutcome<T> {
  event: ScanEvent;
  kind: ScanOutcomeKind;
  /** Present only for added. */
  product?: T;
  /** The code the lookup ran with (trimmed, zeros preserved). */
  lookupCode: string;
  /** Short English operator message. Tamil catalogue wording belongs to the UI layer. */
  message: string;
}

export interface ScanCoordinatorOptions<T> {
  lookup: (barcode: string) => Promise<LookupResult<T>>;
  onOutcome: (outcome: ScanOutcome<T>) => void;
  /** Pending events beyond this are rejected visibly (default 50). */
  maxQueue?: number;
  /** Cap for remembered event ids (default 2000, oldest evicted first). */
  maxSeenIds?: number;
  /** Receives the raw error when a lookup throws instead of answering (still a server-error). */
  onInternalError?: (error: unknown) => void;
}

export interface CameraFrameContext {
  source?: ScanSource;
  storeId?: string | null;
  terminalId?: string | null;
  sessionId?: string | null;
}

/** Milliseconds before a product lookup is abandoned and reported as a timeout. */
export const SCAN_TIMEOUT_MS = 12000;

/**
 * The single documented barcode rule: strip surrounding whitespace/BOM/zero-width marks.
 * Everything else — case, leading zeros, inner spacing — passes through to the catalogue.
 */
export function normalizeBarcode(raw: string): string {
  return raw.replace(/^[\s\uFEFF\u200B]+|[\s\uFEFF\u200B]+$/g, '');
}

let fallbackCounter = 0;

export function newScanEventId(): string {
  const cryptoLike = globalThis.crypto as unknown as { randomUUID?: () => string } | undefined;
  if (cryptoLike && typeof cryptoLike.randomUUID === 'function') return cryptoLike.randomUUID();
  fallbackCounter += 1;
  return (
    'scan-' +
    Date.now().toString(36) +
    '-' +
    fallbackCounter.toString(36) +
    '-' +
    Math.floor(Math.random() * 0xffffff).toString(36)
  );
}

/**
 * Map a thrown lookup error to a failure kind. Transport trouble NEVER becomes not-found:
 * unknown barcodes and broken networks take different UI paths by construction.
 */
export function classifyLookupError(error: unknown): ScanFailureKind {
  if (
    typeof DOMException !== 'undefined' &&
    error instanceof DOMException &&
    (error.name === 'AbortError' || error.name === 'TimeoutError')
  ) {
    return 'timeout';
  }
  if (error instanceof ApiError) {
    if (error.status === 401) return 'auth-expired';
    if (error.status === 403) return 'forbidden';
    return 'server-error';
  }
  if (isNetworkError(error)) return 'offline';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return 'server-error';
}

const MESSAGES: Record<ScanOutcomeKind, string> = {
  added: 'Added to the bill.',
  'not-found': 'No product carries this barcode.',
  timeout: 'Lookup timed out. Check the network and retry — nothing was added.',
  offline: 'Device is offline. The bill is kept; billing resumes when the network returns.',
  'auth-expired': 'Session expired. Sign in again — nothing was added.',
  forbidden: 'Lookup not permitted for this user.',
  'server-error': 'Server error during lookup. Retry — nothing was added.',
  'queue-overflow': 'Scan queue is full. Wait for pending lookups, then scan again.',
  'duplicate-suppressed': 'Duplicate delivery ignored.',
  'stale-generation': 'Bill changed while the lookup was in flight. Scan belongs to the old bill.',
  'camera-repeat-suppressed': 'Same label still in view. Use Scan Again for another unit.',
};

/**
 * The coordinator's public shape. The type parameter is the product its lookups resolve to; it is
 * carried by `ScanCoordinatorOptions<T>` and named here so a coordinator and its callers agree on
 * one type rather than each restating it.
 */
export interface ScanCoordinator<_T> {
  ingest(event: NewScanEvent): void;
  ingestCameraFrame(raw: string, ctx?: CameraFrameContext): void;
  /** Deliberate scan-again: the next frame of the same label adds another unit. */
  cameraRescan(): void;
  /** Call on new bill / hold / resume / sign-out so late lookups cannot leak across bills. */
  newGeneration(): number;
  readonly generation: number;
  readonly pendingCount: number;
  /** True once disposed() has run. Callers recreate the coordinator rather than reuse it. */
  readonly disposed: boolean;
  dispose(): void;
}

export function createScanCoordinator<T>(options: ScanCoordinatorOptions<T>): ScanCoordinator<T> {
  const maxQueue = options.maxQueue ?? 50;
  const maxSeenIds = options.maxSeenIds ?? 2000;
  let epoch = 0;
  let seq = 0;
  let disposed = false;
  let pumping = false;
  let active: ScanEvent | null = null;
  const queue: ScanEvent[] = [];
  const seen = new Set<string>();
  let lastCameraCode: string | null = null;

  function remember(id: string): void {
    seen.add(id);
    if (seen.size > maxSeenIds) {
      const oldest = seen.keys().next();
      if (!oldest.done) seen.delete(oldest.value);
    }
  }

  function emit(event: ScanEvent, kind: ScanOutcomeKind, product?: T, message?: string): void {
    if (disposed) return;
    options.onOutcome({
      event,
      kind,
      ...(product === undefined ? {} : { product }),
      lookupCode: event.lookupCode,
      message: message ?? MESSAGES[kind],
    });
  }

  function buildEvent(init: NewScanEvent): ScanEvent {
    return {
      eventId: init.eventId ?? newScanEventId(),
      raw: init.raw,
      lookupCode: normalizeBarcode(init.raw),
      source: init.source,
      storeId: init.storeId ?? null,
      terminalId: init.terminalId ?? null,
      sessionId: init.sessionId ?? null,
      cartGeneration: typeof init.cartGeneration === 'number' ? init.cartGeneration : epoch,
      receivedAt: typeof init.receivedAt === 'number' ? init.receivedAt : Date.now(),
      seq: seq++,
    };
  }

  function outcomeFor(result: LookupResult<T>): { kind: ScanOutcomeKind; product?: T } {
    switch (result.kind) {
      case 'found':
        return { kind: 'added', product: result.product };
      case 'not-found':
        return { kind: 'not-found' };
      case 'timeout':
        return { kind: 'timeout' };
      case 'offline':
        return { kind: 'offline' };
      case 'auth-expired':
        return { kind: 'auth-expired' };
      case 'forbidden':
        return { kind: 'forbidden' };
      case 'server-error':
        return { kind: 'server-error' };
    }
  }

  async function pump(): Promise<void> {
    if (pumping) return;
    pumping = true;
    try {
      while (queue.length > 0) {
        if (disposed) return;
        const event = queue.shift() as ScanEvent;
        active = event;
        if (event.cartGeneration !== epoch) {
          active = null;
          emit(event, 'stale-generation');
          continue;
        }
        let result: LookupResult<T>;
        try {
          result = await options.lookup(event.lookupCode);
        } catch (error) {
          if (options.onInternalError) {
            try {
              options.onInternalError(error);
            } catch {
              // Reporting must never break the pump.
            }
          }
          result = { kind: 'server-error' };
        }
        active = null;
        if (disposed) return;
        // The bill may have changed DURING the lookup: re-check before delivering.
        if (event.cartGeneration !== epoch) {
          emit(event, 'stale-generation');
          continue;
        }
        const mapped = outcomeFor(result);
        emit(event, mapped.kind, mapped.product);
      }
    } finally {
      pumping = false;
      active = null;
    }
  }

  return {
    ingest(init: NewScanEvent): void {
      if (disposed) return;
      const event = buildEvent(init);
      if (seen.has(event.eventId)) {
        emit(event, 'duplicate-suppressed');
        return;
      }
      remember(event.eventId);
      // Empty submissions (double Enter on a cleared field) are input noise, not scans.
      if (event.lookupCode === '') return;
      if (event.cartGeneration !== epoch) {
        emit(event, 'stale-generation');
        return;
      }
      if (queue.length + (active === null ? 0 : 1) >= maxQueue) {
        // Visible rejection with the event attached, so the UI can offer retry (fresh id).
        emit(event, 'queue-overflow');
        return;
      }
      queue.push(event);
      void pump();
    },

    ingestCameraFrame(raw: string, ctx?: CameraFrameContext): void {
      if (disposed) return;
      const code = normalizeBarcode(raw);
      if (code === '') return;
      if (code === lastCameraCode) {
        emit(buildEvent({ raw, source: ctx?.source ?? 'camera' }), 'camera-repeat-suppressed');
        return;
      }
      lastCameraCode = code;
      this.ingest({
        raw,
        source: ctx?.source ?? 'camera',
        storeId: ctx?.storeId,
        terminalId: ctx?.terminalId,
        sessionId: ctx?.sessionId,
      });
    },

    cameraRescan(): void {
      lastCameraCode = null;
    },

    newGeneration(): number {
      epoch += 1;
      lastCameraCode = null;
      return epoch;
    },

    get generation(): number {
      return epoch;
    },

    get pendingCount(): number {
      return queue.length + (active === null ? 0 : 1);
    },

    get disposed(): boolean {
      return disposed;
    },

    dispose(): void {
      disposed = true;
      queue.length = 0;
      active = null;
    },
  };
}
